import { computed, type ComputedRef } from 'vue'
import { resolveLocalePreference } from '@architeia/utils'
import { SECRET_ORG_FALLBACK_LOCALE, SECRET_ORG_LOCALES, type SecretOrgLocale } from '@/i18n'
import { useAccountPreferencesStore } from '@/stores/accountPreferences'

// navigator.languages is not reactive: a change of browser language is picked up on the next
// reload, not live, so this computed never re-runs for it.
function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  return navigator.languages?.length ? navigator.languages : [navigator.language]
}

/**
 * ADR-010 D3: the single derivation of the language Secret Org shows: the account language (or the
 * per-origin hint until it loads), else the browser's, else English. Derived, never stored;
 * `useLocaleSync` is the only thing that applies it. There is no product override.
 */
export function useEffectiveLocale(): {
  effectiveLocale: ComputedRef<SecretOrgLocale>
  /** The account time zone, or null to use the device's. */
  effectiveTimeZone: ComputedRef<string | null>
} {
  const preferences = useAccountPreferencesStore()
  const effectiveLocale = computed(() =>
    resolveLocalePreference(SECRET_ORG_LOCALES, SECRET_ORG_FALLBACK_LOCALE, {
      accountLocale: preferences.locale,
      browserLanguages: browserLanguages()
    })
  )
  const effectiveTimeZone = computed(() => preferences.timeZone)
  return { effectiveLocale, effectiveTimeZone }
}
